import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchStaticJson, resolveAppStaticUrl } from '../../../config/fetchStaticJson';
import classes from './SubjectSelect.module.css';

function domainsUrl() {
  return resolveAppStaticUrl(window.config?.domains, '/jsons/domains.json');
}

export default function SubjectSelect({
  subjectName,
  subjectImageUrl,
  domain: selectedDomainName,
  onSelect,
  label,
  disabled = false,
  id = 'subject-domain-select',
}) {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    fetchStaticJson(domainsUrl())
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.domains) ? data.domains : [];
        setDomains(list);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setDomains([]);
        setLoadError('לא ניתן לטעון את רשימת המקצועות');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const selectedName = useMemo(() => {
    if (!domains.length) return '';
    if (selectedDomainName) {
      const byDomain = domains.find((d) => d.name === selectedDomainName);
      if (byDomain) return byDomain.name;
    }
    const byImage = domains.find((d) => d.icon && d.icon === subjectImageUrl);
    if (byImage) return byImage.name;
    const byText = domains.find((d) => d.text?.he && d.text.he === subjectName);
    if (byText) return byText.name;
    return '';
  }, [domains, selectedDomainName, subjectImageUrl, subjectName]);

  const selectedDomain = domains.find((d) => d.name === selectedName);
  const isDisabled = disabled || loading || Boolean(loadError);
  const triggerLabel = loading
    ? 'טוען מקצועות...'
    : selectedDomain
      ? (selectedDomain.text?.he || selectedDomain.name)
      : 'בחר מקצוע מהרשימה';

  const selectDomain = (name) => {
    if (!name) {
      onSelect({ subjectName: '', subjectImageUrl: '', domain: '' });
      setOpen(false);
      return;
    }
    const domain = domains.find((d) => d.name === name);
    if (!domain) return;
    onSelect({
      subjectName: domain.text?.he || domain.name,
      subjectImageUrl: domain.icon || '',
      domain: domain.name,
    });
    setOpen(false);
  };

  return (
    <div className={classes.Field} ref={rootRef}>
      <label className={classes.Label} htmlFor={id}>
        {label}
      </label>
      <div className={classes.Row}>
        {selectedDomain?.icon ?
          <img
            src={selectedDomain.icon}
            alt=""
            className={classes.Icon}
          />
        : null}
        <button
          type="button"
          id={id}
          className={classes.Select}
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={isDisabled}
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className={selectedDomain ? undefined : classes.Placeholder}>
            {triggerLabel}
          </span>
          <span className={classes.Chevron} aria-hidden="true">▾</span>
        </button>
      </div>
      {open && !isDisabled ?
        <ul className={classes.Menu} role="listbox" aria-labelledby={id}>
          <li role="option" aria-selected={!selectedName}>
            <button
              type="button"
              className={`${classes.Option} ${!selectedName ? classes.OptionSelected : ''}`}
              onClick={() => selectDomain('')}
            >
              בחר מקצוע מהרשימה
            </button>
          </li>
          {domains.map((domain) => {
            const isSelected = domain.name === selectedName;
            return (
              <li key={domain.name} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  className={`${classes.Option} ${isSelected ? classes.OptionSelected : ''}`}
                  onClick={() => selectDomain(domain.name)}
                >
                  {domain.icon ?
                    <img src={domain.icon} alt="" className={classes.OptionIcon} />
                  : null}
                  {domain.text?.he || domain.name}
                </button>
              </li>
            );
          })}
        </ul>
      : null}
      {loadError ?
        <p className={classes.Error} role="alert">{loadError}</p>
      : null}
    </div>
  );
}
